import { useCallback, useState } from "react";
import type { JSX } from "react";
import { useModuleSettings } from "./hooks/useModuleSettings";
import type {
  ModuleSettingField,
  ModuleSettingsEntry,
} from "./types";
import { Icon } from "../../components/ui";

// ── Capitalize helper ───────────────────────────────────────────────────────

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ── Drill-in: individual module settings screen ─────────────────────────────

function ModuleDetailView({
  entry,
  onBack,
  onSave,
}: {
  readonly entry: ModuleSettingsEntry;
  readonly onBack: () => void;
  readonly onSave: (moduleId: string, key: string, value: string) => Promise<void>;
}): JSX.Element {
  const { schema, values } = entry;
  const title = capitalize(entry.label);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-2">
        {/* Back + title */}
        <div className="flex items-center gap-3">
          <button type="button" onClick={onBack} className="cursor-pointer shrink-0">
            <Icon name="chevron-left" size={22} className="text-accent" />
          </button>
          <h2 className="text-lg font-semibold leading-tight text-content">
            {title}
          </h2>
        </div>

        {schema?.description && (
          <p className="text-sm text-content-tertiary mt-1 pl-[34px]">
            {schema.description}
          </p>
        )}

        <div className="h-4" />

        {/* No settings state */}
        {!schema || schema.fields.length === 0 ? (
          <div className="rounded-xl bg-surface-tertiary border border-edge px-4 py-8 text-center">
            <p className="text-sm text-content-tertiary">
              No configurable settings for this module.
            </p>
          </div>
        ) : (
          <>
            {/* Settings fields — iOS grouped style */}
            <div className="rounded-xl bg-surface-tertiary border border-edge overflow-hidden divide-y divide-edge">
              {schema.fields.map((field) => {
                const current = values.find((v) => v.key === field.key);
                const currentValue = current?.value ?? field.defaultValue;

                return (
                  <SettingFieldRow
                    key={field.key}
                    field={field}
                    value={currentValue}
                    onSave={(value) => onSave(entry.module_id, field.key, value)}
                  />
                );
              })}
            </div>

            {/* Field descriptions below the group (iOS footer style) */}
            {schema.fields.map((field) =>
              field.description ? (
                <p key={`desc-${field.key}`} className="text-xs text-content-muted leading-relaxed px-4">
                  {field.description}
                </p>
              ) : null,
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Setting field row (iOS-style) ───────────────────────────────────────────

function SettingFieldRow({
  field,
  value,
  onSave,
}: {
  readonly field: ModuleSettingField;
  readonly value: string;
  readonly onSave: (value: string) => Promise<void>;
}): JSX.Element {
  const [localValue, setLocalValue] = useState(value);
  const [saving, setSaving] = useState(false);
  const dirty = localValue !== value;

  const confirmAndSave = useCallback(async (nextValue: string) => {
    if (field.confirmationMessage) {
      if (!window.confirm(field.confirmationMessage)) return;
    }
    setSaving(true);
    try {
      await onSave(nextValue);
      setLocalValue(nextValue);
    } finally {
      setSaving(false);
    }
  }, [field.confirmationMessage, onSave]);

  const handleSave = useCallback(async () => {
    if (!dirty || saving) return;
    await confirmAndSave(localValue);
  }, [dirty, saving, localValue, confirmAndSave]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") void handleSave();
    },
    [handleSave],
  );

  // Boolean → label + toggle on the right
  if (field.field_type.type === "boolean") {
    const checked = localValue === "true";
    return (
      <div className="flex items-center justify-between px-4 py-3">
        <span className="text-sm text-content">{field.label}</span>
        <button
          type="button"
          role="switch"
          aria-checked={checked}
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={async () => {
            const next = checked ? "false" : "true";
            setLocalValue(next);
            setSaving(true);
            try {
              await onSave(next);
            } finally {
              setSaving(false);
            }
          }}
          className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${
            checked ? "bg-accent" : "bg-[#3a3a3c]"
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition-transform ${
              checked ? "translate-x-5" : "translate-x-0"
            }`}
          />
        </button>
      </div>
    );
  }

  // Enum → label on the left, select dropdown on the right
  if (field.field_type.type === "enum") {
    const { options } = field.field_type;
    return (
      <div className="flex items-center justify-between px-4 py-3 gap-4">
        <span className="text-sm text-content shrink-0">{field.label}</span>
        <div className="flex items-center gap-2">
          <select
            value={localValue}
            onChange={(e) => void confirmAndSave(e.target.value)}
            className="rounded-lg bg-surface-secondary border border-edge px-3 py-1.5 text-sm text-content text-right outline-none focus:border-accent cursor-pointer"
          >
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {saving && (
            <span className="text-xs text-content-tertiary animate-pulse">...</span>
          )}
        </div>
      </div>
    );
  }

  // Number or text → label on the left, input on the right
  const inputType = field.field_type.type === "number" ? "number" : "text";
  const min =
    field.field_type.type === "number" ? (field.field_type.min ?? undefined) : undefined;
  const max =
    field.field_type.type === "number" ? (field.field_type.max ?? undefined) : undefined;

  return (
    <div className="flex items-center justify-between px-4 py-3 gap-4">
      <span className="text-sm text-content shrink-0">{field.label}</span>
      <div className="flex items-center gap-2">
        <input
          type={inputType}
          min={min}
          max={max}
          value={localValue}
          onChange={(e) => { setLocalValue(e.target.value); }}
          onKeyDown={handleKeyDown}
          onBlur={() => void handleSave()}
          className="w-24 rounded-lg bg-surface-secondary border border-edge px-3 py-1.5 text-sm text-content text-right outline-none focus:border-accent"
        />
        {saving && (
          <span className="text-xs text-content-tertiary animate-pulse">...</span>
        )}
      </div>
    </div>
  );
}

// ── Module list item (iOS-style row with chevron) ───────────────────────────

function ModuleListRow({
  entry,
  onClick,
}: {
  readonly entry: ModuleSettingsEntry;
  readonly onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between px-4 py-3.5 text-left cursor-pointer hover:bg-surface-hover transition-colors"
    >
      <span className="text-sm text-content">{capitalize(entry.label)}</span>
      <Icon name="chevron-right" size={18} className="text-content-muted" />
    </button>
  );
}

// ── Main panel ──────────────────────────────────────────────────────────────

export function ModuleSettingsPanel(): JSX.Element {
  const { entries: allEntries, loading, updateSetting } = useModuleSettings();
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);

  // Hide "search" module — it has its own dedicated panel
  const entries = allEntries.filter((e) => e.module_id !== "search");

  const selectedEntry = selectedModuleId
    ? entries.find((e) => e.module_id === selectedModuleId)
    : undefined;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-content-tertiary text-sm animate-pulse">
        Loading module settings...
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-content-tertiary text-sm">
        No modules installed.
      </div>
    );
  }

  // Drill-in view
  if (selectedEntry) {
    return (
      <ModuleDetailView
        entry={selectedEntry}
        onBack={() => { setSelectedModuleId(null); }}
        onSave={updateSetting}
      />
    );
  }

  // Module list
  return (
    <div className="px-6 py-5 space-y-2">
      <h2 className="text-lg font-semibold text-content">Modules</h2>
      <p className="text-sm text-content-tertiary">
        Configure settings for installed modules.
      </p>

      <div className="h-2" />

      <div className="rounded-xl bg-surface-tertiary border border-edge overflow-hidden divide-y divide-edge">
        {entries.map((entry) => (
          <ModuleListRow
            key={entry.module_id}
            entry={entry}
            onClick={() => { setSelectedModuleId(entry.module_id); }}
          />
        ))}
      </div>
    </div>
  );
}
